// ~components/surface/base-surface.tsx
import { Box, BoxProps, styled } from '@mui/material';

export type BaseSurfaceProps = BoxProps & {
  variant?: 'outlined' | 'premium';
  interactive?: boolean;
  radius?: number | string;
};

const StyledBaseSurface = styled(Box, {
  shouldForwardProp: (prop) =>
    !['sx', 'variant', 'interactive', 'radius'].includes(String(prop)),
})<BaseSurfaceProps>(({
  theme,
  sx = [],
  variant = 'outlined',
  interactive,
  radius,
}) => {
  const radiusPx = radius ?? theme.spacing(1.5);
  const shadow =
    theme.customShadows?.premium ?? '0px 4px 20px 5px rgba(0,0,0,0.04)';
  const shadowHover =
    theme.customShadows?.premiumHover ?? '0px 6px 28px 8px rgba(0,0,0,0.06)';
  const shadowActive =
    theme.customShadows?.premiumActive ?? '0px 3px 14px 4px rgba(0,0,0,0.06)';

  const common = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    borderRadius: radiusPx,
    padding: theme.spacing(1.5),
    boxSizing: 'border-box',
    // [theme.breakpoints.up('sm')]: { padding: theme.spacing(1.5) },
  };

  const outlined = {
    backgroundColor: theme.palette.fog['95'],
    border: `1px solid ${theme.palette.fog['85']}`,
    ...(interactive && {
      transition: '0.2s all',
      cursor: 'pointer',
      '&:hover': {
        border: `1px solid ${theme.palette.primaryTokens[100]}`,
        backgroundColor: theme.palette.neutral[100],
      },
    }),
  };

  const premium = {
    backgroundColor: theme.palette.neutral[100],
    border: '1px solid transparent',
    boxShadow: shadow,
    transition: 'opacity .3s ease, visibility .3s ease, box-shadow .3s ease',
    ...(interactive && {
      cursor: 'pointer',
      '&:hover': { boxShadow: shadowHover },
      '&:active': { boxShadow: shadowActive },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: theme.palette.primaryTokens[100],
        outlineOffset: 2,
      },
    }),
  };

  return {
    ...common,
    ...(variant === 'premium' ? premium : outlined),
    ...(Array.isArray(sx) ? sx : [sx]),
  };
});

export const BaseSurface = ({
  interactive,
  role,
  onClick,
  ...props
}: BaseSurfaceProps) => (
  <StyledBaseSurface
    interactive={interactive}
    onClick={onClick}
    role={role ?? (interactive || onClick ? 'button' : undefined)}
    {...props}
  />
);
